import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { app, auth } from '@/services/firebaseService';

/**
 * KB-2611 — fans pro click events into the internal `recordUiAction` sink
 * (kb-ff `firebase/functions/shared/ui_action_validation.js`), alongside
 * GA4, so pro traffic joins marketing and Flutter in the internal analytics
 * pipeline. Two callers: `emitEvent.ts` (every click event, see its docblock
 * for why this module never throws back into it) and `internalPageView.ts`
 * (the KB-4771 page view, which deliberately skips GA4).
 *
 * ## Discriminator: a `page` PREFIX, not a new key (AC1)
 *
 * `recordUiAction` has no surface allowlist — origin is carried by `page` +
 * `env`, both already on `KNOWN_KEYS`. Prefixing `page` with `pro:` (e.g.
 * `pro:/admin/organizations/abc123`) distinguishes pro from marketing
 * (`/find-camps`) and Flutter (app route paths) using a field every event
 * already carries: no new key, no KNOWN_KEYS change, no `unknown_key`
 * log-noise risk. Checked before choosing this: no downstream consumer
 * (`daily_kpis.js`, `usage_daily_writes.js`, `search_history_doc_count.js`)
 * parses `page` by exact match or a fixed prefix, so this is additive and
 * safe. A new key was the alternative, and the module's own docblock is
 * explicit about the cost of getting that two-step change wrong — this
 * ticket doesn't need to pay it.
 *
 * ## `internal: 1` — pro traffic must never inflate customer KPIs
 *
 * Pro is logged-in-only and used exclusively by KidsBooked staff (see
 * `google-analytics-init.ts`'s docblock) — it is not customer/visitor
 * traffic. `dailyKpis` / `anonUsageDaily` read this same sink to measure
 * customer acquisition and search funnels. `internal: 1` keeps pro's clicks
 * out of those aggregates (`countsTowardProdMetrics` in kb-ff's
 * `env_resolution.js` is `env === 'prod' && internal !== 1`) while still
 * landing in the internal-only mirror collection for pro-specific analysis
 * (`isInternalOnlyWrite`). It is never 0 — there is no pro traffic that
 * should count as a customer KPI.
 *
 * ## `env` — taken verbatim by the server, and why that's correct here (AC3)
 *
 * `env_resolution.js` resolves environment from the request's Origin/Referer
 * host FIRST, falling back to this client claim only when the host is
 * unrecognised. Pro's own hosts (`pro.kidsbooked.com` prod,
 * `kb-staging-proj.firebaseapp.com` staging — see `firebaseService.ts`) are
 * not in that module's host tables, so for pro traffic the header check
 * always produces no evidence and this claim decides. It MUST be correct.
 *
 * Mapped from `VITE_ENVIRONMENT` (`development|test|staging|production` —
 * `sentry-environment.ts`'s vocabulary) to the server's
 * (`dev|staging|prod` — `env_resolution.js`'s `VALID_ENVS`). `test` maps to
 * `dev`: a `test`-mode build is CI/local, never real traffic, and `dev` is
 * where env_resolution.js already routes anything it can't positively place.
 */
function resolveInternalEnv(viteEnvironment: string | undefined): 'dev' | 'staging' | 'prod' {
  switch (viteEnvironment) {
    case 'production':
      return 'prod';
    case 'staging':
      return 'staging';
    case 'development':
    case 'test':
    default:
      return 'dev';
  }
}

export type InternalUiActionParams = Record<string, string | number | boolean>;

export interface InternalUiActionOptions {
  /**
   * Replaces `window.location.pathname` in `page` (still `pro:`-prefixed). The
   * page view passes its route pattern here so the row carries no document id.
   */
  page?: string;
}

let functionsInstance: Functions | null = null;
function recordUiActionCallable() {
  if (!functionsInstance) {
    functionsInstance = getFunctions(app, 'us-east4');
  }
  return httpsCallable(functionsInstance, 'recordUiAction');
}

/**
 * Fire-and-forget: never awaited by the caller, never throws, never lets a
 * rejected callable become an unhandled promise rejection. `recordUiAction`
 * requires an authenticated caller — this is a no-op before login (the
 * GA4 send for the same event still goes through).
 */
export function sendInternalUiAction(
  action: string,
  params?: InternalUiActionParams,
  options?: InternalUiActionOptions,
): void {
  if (typeof window === 'undefined') return;
  if (!auth.currentUser) return;

  try {
    recordUiActionCallable()({
      action,
      page: `pro:${options?.page ?? window.location.pathname}`,
      env: resolveInternalEnv(import.meta.env.VITE_ENVIRONMENT),
      internal: 1,
      ...(params ? { extra: params } : {}),
    }).catch((err: unknown) => {
      console.warn('[analytics] recordUiAction failed', err);
    });
  } catch (err) {
    console.warn('[analytics] recordUiAction call failed to start', err);
  }
}
